import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { useResizeObserverState } from '../../../../../hooks/useResizeObserverState';
import styles from './index.module.scss';

const StickySyncedScrollLayout = ({
  className,
  topOffset,
  header,
  children,
  rootId,
}) => {
  const RAFRef = useRef(0);
  const isScrollSyncRef = useRef(false);

  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const scrollRef = useRef(null);

  const scrollWidth = useResizeObserverState(bodyRef, {
    compute: (el) => {
      if (el) {
        const scrollWidth = el.scrollWidth;
        const width = el.getBoundingClientRect().width;
        return scrollWidth - width >= 1 ? scrollWidth : width;
      }
    },
    defaultValue: 0,
  });

  const scheduleScrollSyncReset = () => {
    if (!RAFRef.current) {
      RAFRef.current = requestAnimationFrame(() => {
        RAFRef.current = 0;
        isScrollSyncRef.current = false;
      });
    }
  };

  const onHeaderScroll = () => {
    syncScroll(
      headerRef,
      [bodyRef, scrollRef],
      isScrollSyncRef,
      scheduleScrollSyncReset
    );
  };

  const onBodyScroll = () => {
    syncScroll(
      bodyRef,
      [headerRef, scrollRef],
      isScrollSyncRef,
      scheduleScrollSyncReset
    );
  };

  const onScroll = () => {
    syncScroll(
      scrollRef,
      [headerRef, bodyRef],
      isScrollSyncRef,
      scheduleScrollSyncReset
    );
  };

  useEffect(() => {
    if (RAFRef.current) cancelAnimationFrame(RAFRef.current);
  }, []);

  return (
    <div className={cn(styles.root, className)} id={rootId}>
      {header && (
        <div
          ref={headerRef}
          style={{ '--sticky-layout-header-top': resolveOffset(topOffset) }}
          className={styles.header}
          onScroll={onHeaderScroll}
        >
          {header}
        </div>
      )}

      <div ref={bodyRef} className={styles.body} onScroll={onBodyScroll}>
        {children}
      </div>

      <div ref={scrollRef} className={styles.scroll} onScroll={onScroll}>
        <div style={{ width: scrollWidth }} className={styles.scrollInner} />
      </div>
    </div>
  );
};

export default StickySyncedScrollLayout;

function syncScroll(sourceRef, targetRefs, isScrollSyncRef, cb) {
  const source = sourceRef.current;

  if (source && targetRefs?.length && !isScrollSyncRef.current) {
    isScrollSyncRef.current = true;
    targetRefs.forEach((ref) => {
      if (ref.current) {
        ref.current.scrollLeft = source.scrollLeft;
      }
    });
    cb();
  }
}

function resolveOffset(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return 0;

  if (typeof value === 'number' || !isNaN(value)) return `${value}px`;

  return value;
}
